import React, { useState, useEffect, useRef } from 'react';
import { getPcmCdNmByPcmCdDivisionFromMasterInfo } from '../../../redux/reducers/MasterInfoReducer/utils/MasterInfoStateUtils';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { setAmountComma, setStringToBrackets } from '../../../utils/StringUtil';
import { Element as ScrollTargeElement, scroller } from 'react-scroll';
import Grid from '../../atoms/Grid';
import Table from '../../atoms/Table';
import Button from '../../atoms/Button';

import Spacer from '../../atoms/Spacer';
import PreviewContents from '../../atoms/PreviewContents';
import PreviewMiddleCategoryHeader from '../../atoms/PreviewMiddleCategoryHeader';
import PreviewSmallCategoryHeader from '../../atoms/PreviewSmallCategoryHeader';
import ThumbnailCard from '../../molecules/ThumbnailCard';
import PreviewHeader from '../../atoms/PreviewHeader';
import ImageViewerDrawer from '../ImageViewDrawer';
import ProductInfoPreview from '../ProductInfoPreview';
import ActionInfoDetailPreview from '../ActionInfoDetailPreview';

import { AppState } from '../../../redux/modules/store';
import { AttachedFileState, QuotationPdfState } from '../../../redux/reducers/CustomerInfoAllReducer';
import { StringOrNull } from '../../../types';
import { AttachedFile } from '../../../interfaces/AttachedFile';
import { isCrewModule } from '../../../utils/Env';
import { SCROLL_TARGET_TYPE } from '../../../Common';
import { ScrollTargetInfo } from '../../templates/SummaryPreviewCommon';
export interface NegotiationDetailPreviewProps {
  defaultViewportMetaContent: string;
  userScalableViewportMetaContent: string;
  scrollContainerId: string;
  isEditable: boolean;
  onModal?: boolean;
  /**
   * 初期スクロール情報
   */
  scrollTargetInfo?: ScrollTargetInfo;
  openPermissionModal?: (type: string, negotiationId?: string, productId?: string, actionId?: string) => void;
}

const NegotiationDetailPreview: React.FC<NegotiationDetailPreviewProps> = ({
  defaultViewportMetaContent,
  userScalableViewportMetaContent,
  scrollContainerId,
  isEditable,
  onModal = false,
  scrollTargetInfo,
  openPermissionModal,
}) => {
  const hyphens = '----------';
  const lastDivElementOffsetValue = 180;
  const scrollTargetElementNamePrefix = 'scroll-target';

  // custom Hooks
  const masterInfo = useSelector((state: AppState) => state.masterInfo);
  const customerInfoAll = useSelector((state: AppState) => state.customerInfoAll);
  const history = useHistory();

  // local State
  const [isOpenViewerDrawer, setIsOpenViewerDrawer] = useState<boolean>(false);
  const [imageViewIndex, setImageViewIndex] = useState<number>(0);
  const [imageViewFiles, setImageViewFiles] = useState<AttachedFile[]>([]);
  const [lastDivItemStyleHeight, setLastDivItemStyleHeight] = useState<number>(0);
  const lastDivItemRef = useRef<HTMLDivElement>(null);
  const initialScrollTargetElementName = scrollTargetInfo ? `${scrollTargetElementNamePrefix}-${scrollTargetInfo.scrollTargetType}` : null;

  const beforeOpenDrawer = (files: AttachedFileState[]) => {
    setImageViewFiles([]);
    setImageViewFiles(
      files.map((v) => {
        return {
          fileType: '01',
          dataObject: v.dataObject || '',
          fileName: v.fileName,
        };
      }),
    );
  };

  // 案件名
  const displayProjectName = (projectCd: StringOrNull, registeredFromType: StringOrNull) =>
    `${getPcmCdNmByPcmCdDivisionFromMasterInfo(masterInfo, '0154', projectCd || '') || ''}${registeredFromType === '1' ? '★' : ''}`;

  // 初回登録者
  const displayFirstClosingSalesmanName = (firstClosingSalesmanCd: StringOrNull, firstClosingSalesmanName: StringOrNull) => {
    if (!firstClosingSalesmanCd && !firstClosingSalesmanName) {
      return '';
    }
    const displayFirstClosingSalesmanName = firstClosingSalesmanName || hyphens;
    const displayFirstClosingSalesmanCd = setStringToBrackets(firstClosingSalesmanCd || hyphens);

    return `${displayFirstClosingSalesmanName}${displayFirstClosingSalesmanCd}`;
  };

  // 担当者
  const displayClosingSalesmanName = (
    closingSalesmanCd: StringOrNull,
    closingSalesmanName: StringOrNull,
    otherSalesmanCd: StringOrNull,
    otherSalesmanName: StringOrNull,
  ): string => {
    if (!closingSalesmanName && !otherSalesmanName && !otherSalesmanCd) {
      return '';
    }

    if (closingSalesmanName) {
      const displayClosingSalesmanCd = setStringToBrackets(closingSalesmanCd || hyphens);
      return `${closingSalesmanName}${displayClosingSalesmanCd}`;
    }
    const displayOtherClosingSalesmanName = otherSalesmanName || hyphens;
    const displayOtherClosingSalesmanCd = setStringToBrackets(otherSalesmanCd || hyphens);
    return `${displayOtherClosingSalesmanName}${displayOtherClosingSalesmanCd}`;
  };

  // 臨時コード
  const displayTempCd = (tempCd1: StringOrNull, tempCd2: StringOrNull, tempCd3: StringOrNull, tempCd4: StringOrNull): string => {
    const lineBreak = '\n';
    const tempCdList: string[] = [];
    if (tempCd1 && tempCd1 !== '0') {
      tempCdList.push(`臨時コード①：${tempCd1}`);
    }
    if (tempCd2) {
      tempCdList.push(`臨時コード②：${tempCd2}`);
    }
    if (tempCd3) {
      tempCdList.push(`臨時コード③：${tempCd3}`);
    }
    if (tempCd4) {
      tempCdList.push(`臨時コード④：${tempCd4}`);
    }

    return tempCdList.join(lineBreak);
  };

  /**
   * 商談成立条件
   */
  const displaySatisfiedCriteria = (
    contractPlanNoMeasure: boolean,
    cashBack: boolean,
    productDiscount: boolean,
    other: boolean,
    otherMemo: StringOrNull,
  ) => {
    const lineBreak = '\n';
    const tempCriteriaList: string[] = [];
    if (contractPlanNoMeasure) {
      tempCriteriaList.push('追加施策必要なくご契約予定');
    }
    if (cashBack) {
      tempCriteriaList.push('キャッシュバック（増額含む）');
    }
    if (productDiscount) {
      tempCriteriaList.push('端末値引き');
    }
    if (other) {
      tempCriteriaList.push(`その他${otherMemo ? setStringToBrackets(isCrewModule() ? otherMemo : '入力あり') : ''}`);
    }
    return tempCriteriaList.join(lineBreak);
  };

  /**
   * 新規タブでPDFを表示する
   * @param base64String
   */
  const openPdfNewWindow = (base64String: string) => {
    fetch(base64String)
      .then((res) => res.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        document.body.appendChild(a);
        a.setAttribute('href', url);
        a.setAttribute('target', '_blank');
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      });
  };

  const onShowPdf = (quotationPdf: QuotationPdfState) => {
    // pdfData(バイナリデータ)
    const base64String = `data:application/pdf;base64,${quotationPdf.dataObject}`;
    // 印刷可否フラグ (※可能の場合1をつける、その他の値は不可)
    const enable_print_flg = '1';
    // POST or GET
    const http_method = 'GET';
    // ジニーブラウザの場合は、external.ShowPDFInfoByBase64を実行。
    // ジニーブラウザでない場合は、新規タブでPDFを表示する
    window.external.ShowPDFInfoByBase64
      ? window.external.ShowPDFInfoByBase64(base64String, enable_print_flg, http_method)
      : openPdfNewWindow(base64String);
  };

  const scrollToElementByOffset = (targetElementName: string, offset: number) => {
    scroller.scrollTo(targetElementName, {
      duration: 600,
      delay: 100,
      smooth: true,
      containerId: scrollContainerId,
      offset: offset,
    });
  };
  // スクロール確認は目視確認項目のため、カバレッジ集計対象外とする
  /* istanbul ignore next */
  const init = () => {
    lastDivItemRef.current && setLastDivItemStyleHeight(lastDivElementOffsetValue + lastDivItemRef.current.clientHeight);
    // 初期スクロール処理
    if (scrollTargetInfo) {
      const initialScrollElement = `${initialScrollTargetElementName}-${scrollTargetInfo.id}`;
      const offsetValue: number = scrollTargetInfo.scrollTargetType === SCROLL_TARGET_TYPE.NEGOTIATION ? 0 : -50;
      scrollToElementByOffset(initialScrollElement, offsetValue);
    }
  };
  const cleanUp = () => {
    /**
     * do nothing
     */
  };

  useEffect(() => {
    init();
    return cleanUp;
  }, []);

  const lastDivStyle = `calc(100vh - ${lastDivItemStyleHeight}px )`;

  return (
    <>
      {/* 画像表示Drawer */}
      {isCrewModule() && isOpenViewerDrawer && (
        <ImageViewerDrawer
          isOpen={isOpenViewerDrawer}
          index={imageViewIndex}
          attachedFileList={imageViewFiles}
          onClose={() => setIsOpenViewerDrawer(false)}
          isReadOnly={true}
          onModal={onModal}
          defaultViewportMetaContent={defaultViewportMetaContent}
          userScalableViewportMetaContent={userScalableViewportMetaContent}
          data-testid='image-readonly-drawer'
          data-testid-e2e='image-readonly-drawer'
        />
      )}
      {customerInfoAll.negotiationInfoDetail.length > 0 &&
        [...customerInfoAll.negotiationInfoDetail]
          .sort((a, b) => a.negotiationId - b.negotiationId)
          .map((negotiationItem, negotiationIndex) => {
            return (
              <>
                <div
                  key={negotiationItem.negotiationId}
                  id={'negotiation' + negotiationItem.negotiationId}
                  className='container u-bg-white u-pt8 u-mt24 preview-header'>
                  {/* スクロールターゲット */}
                  <ScrollTargeElement
                    name={`${scrollTargetElementNamePrefix}-${SCROLL_TARGET_TYPE.NEGOTIATION}-${negotiationItem.negotiationId}`}
                  />
                  {/* ヘッダー */}
                  <div key={negotiationIndex} className='u-bg-white preview-header__sticky'>
                    <PreviewHeader
                      uniqueId={`negotiation-preview-header-${negotiationIndex}`}
                      title={negotiationItem.negotiationName || ''}
                      detailInfo={
                        isEditable
                          ? {
                              label: `詳細`,
                              onClick: () => {
                                history.push(`/negotiation/detail/${negotiationItem.negotiationId}`);
                              },
                            }
                          : undefined
                      }
                    />
                  </div>

                  <div
                    ref={
                      customerInfoAll.negotiationInfoDetail.length - 1 === negotiationIndex &&
                      !negotiationItem.productInfoDetail.length &&
                      !negotiationItem.actionInfoDetail.length
                        ? lastDivItemRef
                        : null
                    }
                    id={'negotiationInfo' + negotiationItem.negotiationId}>
                    <Table className='preview-table'>
                      {/* header (middle) */}
                      <PreviewMiddleCategoryHeader
                        editInfo={
                          isEditable
                            ? {
                                uniqueId: `${negotiationItem.negotiationId}`,
                                label: '編集',
                                onClickEditHandler: () =>
                                  openPermissionModal && openPermissionModal('onEditNego', `${negotiationItem.negotiationId}`),
                              }
                            : undefined
                        }
                        title='商談情報'
                        uniqueId={`negotiation-header-${negotiationIndex}`}
                      />

                      <Table.TBody>
                        {/* header (small) */}
                        <PreviewSmallCategoryHeader title='基本情報' uniqueId={`negotiation-base-info-heading-${negotiationIndex}`} />

                        {/* preview contents */}
                        <PreviewContents
                          title='案件名'
                          uniqueId={`negotiation-product-name-${negotiationIndex}`}
                          paragraphValue={displayProjectName(negotiationItem.projectCd, negotiationItem.registeredFromType)}
                        />

                        <PreviewContents
                          title='初回登録者'
                          uniqueId={`negotiation-first-closing-salesman-name-${negotiationIndex}`}
                          paragraphValue={displayFirstClosingSalesmanName(
                            negotiationItem.firstClosingSalesmanCd,
                            negotiationItem.firstClosingSalesmanName,
                          )}
                        />

                        <PreviewContents
                          title='担当者'
                          uniqueId={`negotiation-closing-salesman-name-${negotiationIndex}`}
                          paragraphValue={displayClosingSalesmanName(
                            negotiationItem.closingSalesmanCd,
                            negotiationItem.closingSalesmanName,
                            negotiationItem.otherSalesmanCd,
                            negotiationItem.otherSalesmanName,
                          )}
                        />
                        {/* ご案内書類スクロール（表示位置調整のためこの位置に配置） */}
                        <ScrollTargeElement
                          name={`${scrollTargetElementNamePrefix}-${SCROLL_TARGET_TYPE.NEGOTIATION_PHOTO}-${negotiationItem.negotiationId}`}
                        />
                        <PreviewContents
                          title='臨時コード'
                          uniqueId={`negotiation-temp-cd-${negotiationIndex}`}
                          paragraphValue={displayTempCd(
                            negotiationItem.tempCd1,
                            negotiationItem.tempCd2,
                            negotiationItem.tempCd3,
                            negotiationItem.tempCd4,
                          )}
                        />
                      </Table.TBody>

                      {/* ご案内書類 */}
                      <Table.TBody>
                        {/* header (small) */}
                        <PreviewSmallCategoryHeader title='ご案内書類' uniqueId={`negotiation-attached-file-heading-${negotiationIndex}`} />
                        {/* preview contents */}
                        {/* ご案内書類は crewモジュールである場合にのみ、サムネイルを出力する、それ以外の場合は画像の枚数を表示します */}
                        <PreviewContents
                          title='ご案内書類'
                          uniqueId={`negotiation-attached-file-${negotiationIndex}`}
                          paragraphValue={
                            !isCrewModule() && negotiationItem.attachedFileList.length > 0
                              ? `${negotiationItem.attachedFileList.length}枚`
                              : undefined
                          }>
                          {isCrewModule() && (
                            <Grid>
                              <Grid.Row>
                                {negotiationItem.attachedFileList.map((attachedFile: AttachedFileState, index: number) => {
                                  return (
                                    <Grid.Col
                                      className='u-mb24'
                                      colS={12}
                                      colM={4}
                                      colL={3}
                                      key={attachedFile.sequenceId}
                                      onClick={() => {
                                        beforeOpenDrawer(negotiationItem.attachedFileList);
                                        setImageViewIndex(index);
                                        setIsOpenViewerDrawer(true);
                                      }}
                                      data-testid={`negotiation-attached-file-col-${index}`}
                                      data-testid-e2e={`negotiation-attached-file-col-${index}`}>
                                      <ThumbnailCard
                                        fileName={attachedFile.fileName}
                                        dataObject={attachedFile.dataObject || ''}
                                        index={index + 1}
                                        numberOfthumbnails={negotiationItem.attachedFileList.length}
                                        showAll={true}
                                      />
                                    </Grid.Col>
                                  );
                                })}
                              </Grid.Row>
                            </Grid>
                          )}
                        </PreviewContents>
                      </Table.TBody>

                      {/* 特典案内 内容 */}
                      <Table.TBody>
                        {/* header (small) */}
                        <PreviewSmallCategoryHeader
                          title='特典案内'
                          uniqueId={`negotiation-special-favor-contents-heading-${negotiationIndex}`}
                        />
                        {/* preview contents */}
                        <PreviewContents
                          title='特典案内'
                          uniqueId={`negotiation-special-favor-contents-${negotiationIndex}`}
                          paragraphValue={negotiationItem.specialFavorContents || ''}
                        />
                      </Table.TBody>

                      {/* お客さま還元金額 */}
                      <Table.TBody>
                        {/* header (small) */}
                        <PreviewSmallCategoryHeader
                          title='お客さま還元金額'
                          uniqueId={`negotiation-pay-back-amount-contents-heading-${negotiationIndex}`}
                        />
                        {/* preview contents */}
                        <PreviewContents
                          title='お客さま還元金額'
                          uniqueId={`negotiation-pay-back-amount-contents-${negotiationIndex}`}
                          paragraphValue={
                            negotiationItem.payBackAmount !== null ? `${setAmountComma(negotiationItem.payBackAmount.toString())}円` : ''
                          }
                        />
                      </Table.TBody>

                      {/* 商談成立条件 */}
                      <Table.TBody>
                        {/* header (small) */}
                        <PreviewSmallCategoryHeader
                          title='商談成立条件'
                          uniqueId={`negotiation-satisfied-criteria-contents-heading-${negotiationIndex}`}
                        />
                        {/* preview contents */}
                        <PreviewContents
                          title='商談成立条件'
                          uniqueId={`negotiation-satisfied-criteria-contents-${negotiationIndex}`}
                          paragraphValue={
                            displaySatisfiedCriteria(
                              negotiationItem.satisfiedCriteriaContractPlanNoMeasure,
                              negotiationItem.satisfiedCriteriaCashBack,
                              negotiationItem.satisfiedCriteriaProductDiscount,
                              negotiationItem.satisfiedCriteriaOther,
                              negotiationItem.satisfiedCriteriaOtherMemo,
                            ) || ''
                          }
                        />
                      </Table.TBody>

                      {/* メモ */}
                      <Table.TBody>
                        {/* header (small) */}
                        <PreviewSmallCategoryHeader title='メモ' uniqueId={`negotiation-memo-heading-${negotiationIndex}`} />
                        {/* preview contents */}
                        <PreviewContents
                          title='メモ'
                          uniqueId={`negotiation-memo-${negotiationIndex}`}
                          paragraphValue={negotiationItem.negotiationNote || ''}
                        />
                      </Table.TBody>

                      {/* 見積書 */}
                      <Table.TBody>
                        {/* header (small) */}
                        <PreviewSmallCategoryHeader title='見積書' uniqueId={`negotiation-quotation-heading-${negotiationIndex}`} />

                        {/* preview contents */}
                        <PreviewContents title='見積書' uniqueId={`negotiation-quotation-${negotiationIndex}`}>
                          <div>
                            {negotiationItem.cncgPrintNo && (
                              <p
                                data-testid={`negotiation-quotation-cncg-print-no-${negotiationIndex}-value`}
                                data-testid-e2e={`negotiation-quotation-cncg-print-no-${negotiationIndex}-value`}>
                                見積書番号：{negotiationItem.cncgPrintNo}
                              </p>
                            )}
                            {negotiationItem.quotationPdf && (
                              <div>
                                {negotiationItem.quotationPdf.cncgStatus && (
                                  <p
                                    data-testid={`negotiation-quotation-cncg-status-${negotiationIndex}-value`}
                                    data-testid-e2e={`negotiation-quotation-cncg-status-${negotiationIndex}-value`}>
                                    連携ステータス：
                                    {negotiationItem.quotationPdf.cncgStatus === '1' ? '連携中' : '連携完了'}
                                  </p>
                                )}
                                {negotiationItem.quotationPdf.cncgStatus === '2' && negotiationItem.quotationPdf.dataObject && (
                                  <Button
                                    className='u-mln4'
                                    type='transparent'
                                    label={<span className='u-text-underline'>見積書のPDFを表示</span>}
                                    rightIcon='picture_as_pdf'
                                    onClick={() => onShowPdf(negotiationItem.quotationPdf as QuotationPdfState)}
                                    data-testid={`negotiation-quotation-open-file-button-${negotiationIndex}`}
                                    data-testid-e2e={`negotiation-quotation-open-file-button-${negotiationIndex}`}
                                  />
                                )}
                              </div>
                            )}
                          </div>
                        </PreviewContents>
                      </Table.TBody>

                      {/* ヒアナビアンケート */}
                      <Table.TBody>
                        {/* header (small) */}
                        <PreviewSmallCategoryHeader
                          title='ヒアナビアンケートID'
                          uniqueId={`negotiation-hearing-id-hear-navi-heading-${negotiationIndex}`}
                        />
                        {/* preview contents */}
                        <PreviewContents
                          title='ヒアナビアンケートID'
                          uniqueId={`negotiation-hearing-id-hear-navi-${negotiationIndex}`}
                          paragraphValue={negotiationItem.hearingIdHearNavi || ''}
                        />
                      </Table.TBody>

                      {/* スケジュール管理ヒアリング結果 */}
                      <Table.TBody>
                        {/* header (small) */}
                        <PreviewSmallCategoryHeader
                          title='スケジュール管理ヒアリング結果'
                          uniqueId={`negotiation-shop-call-no-schedule-heading-${negotiationIndex}`}
                        />
                        {/* preview contents */}
                        <PreviewContents
                          title='スケジュール管理ヒアリング結果'
                          uniqueId={`negotiation-shop-call-no-schedule-${negotiationIndex}`}
                          paragraphValue={
                            negotiationItem.shopCallNoSchedule && negotiationItem.hearingIdSchedule
                              ? `${negotiationItem.shopCallNoSchedule}_${negotiationItem.hearingIdSchedule}`
                              : ''
                          }
                        />
                      </Table.TBody>

                      {/* 旧見込からの連携項目 */}
                      <Table.TBody>
                        {/* header (small) */}
                        <PreviewSmallCategoryHeader
                          title='旧見込からの連携項目'
                          uniqueId={`negotiation-linked-item-old-pcm-heading-${negotiationIndex}`}
                        />
                        {/* preview contents */}
                        <PreviewContents
                          title='旧見込からの連携項目'
                          uniqueId={`negotiation-linked-item-old-pcm-${negotiationIndex}`}
                          paragraphValue={negotiationItem.linkedItemOldPcm || ''}
                        />
                      </Table.TBody>
                    </Table>
                  </div>
                  {negotiationItem.productInfoDetail &&
                    [...negotiationItem.productInfoDetail]
                      .sort((a, b) => a.productId - b.productId)
                      .map((item, index) => {
                        return (
                          <div key={index}>
                            {/* スクロールターゲット */}
                            <ScrollTargeElement name={`${scrollTargetElementNamePrefix}-${SCROLL_TARGET_TYPE.PRODUCT}-${item.productId}`} />
                            <ProductInfoPreview
                              ref={
                                customerInfoAll.negotiationInfoDetail.length - 1 === negotiationIndex &&
                                negotiationItem.productInfoDetail.length - 1 === index &&
                                !negotiationItem.actionInfoDetail.length
                                  ? lastDivItemRef
                                  : null
                              }
                              item={item}
                              index={index}
                              negotiationId={negotiationItem.negotiationId}
                              isEditable={isEditable}
                              key={item.productId}
                              openPermissionModal={openPermissionModal}
                            />
                          </div>
                        );
                      })}
                  {negotiationItem.actionInfoDetail &&
                    [...negotiationItem.actionInfoDetail]
                      .sort((a, b) => a.actionId - b.actionId)
                      .map((item, index) => {
                        return (
                          <div key={index}>
                            {/* スクロールターゲット */}
                            <ScrollTargeElement name={`${scrollTargetElementNamePrefix}-${SCROLL_TARGET_TYPE.ACTION}-${item.actionId}`} />
                            <ActionInfoDetailPreview
                              ref={
                                customerInfoAll.negotiationInfoDetail.length - 1 === negotiationIndex &&
                                negotiationItem.actionInfoDetail.length - 1 === index
                                  ? lastDivItemRef
                                  : null
                              }
                              actionItem={item}
                              index={index}
                              negotiationId={negotiationItem.negotiationId}
                              isEditable={isEditable}
                              key={item.actionId}
                              openPermissionModal={openPermissionModal}
                            />
                          </div>
                        );
                      })}
                </div>
                {/* スクロール位置がずれているため調整 */}
                {customerInfoAll.negotiationInfoDetail.length - 1 === negotiationIndex && lastDivItemRef.current && (
                  <Spacer minHeight={lastDivStyle} />
                )}
              </>
            );
          })}
    </>
  );
};

export default NegotiationDetailPreview;
